/**
 * Geometry of "Trace the symbol" (PlayspaceSimbolDraw.cs /
 * PlayspaceDrawSimbolController.cs / PlayspacePointItemController.cs), kept
 * free of React so the e2e tests can use it too.
 *
 * A symbol's `points` are the Unity DrawSimbol.elements: an entry with
 * `down` starts a new stroke (its position is not used); the others are the
 * stroke's points, in the symbol picture's pixels (a 1024 px texture, origin
 * at the centre, y up). z = 0 is a main point, z < 0 a sub-point (smaller,
 * reached from further away).
 */
import type { DrawSimbol } from "@makeit/schema";

export const STAGE_W = 2048;
export const STAGE_H = 1536;
/** Size of the texture the points were recorded on (the symbol pictures, imported at 1024). */
const TEX = 1024;
const GAP = 80;

export interface TracePoint {
  x: number;
  y: number;
  sub: boolean;
}
export interface Box {
  left: number;
  top: number;
  size: number;
}

/** The strokes of a symbol: lists of points, in the picture's units. */
export function strokesOf(simbol: Pick<DrawSimbol, "points"> | null | undefined): { x: number; y: number; sub: boolean }[][] {
  const strokes: { x: number; y: number; sub: boolean }[][] = [];
  for (const p of simbol?.points ?? []) {
    if (p.down || strokes.length === 0) strokes.push([]);
    if (!p.down) strokes[strokes.length - 1].push({ x: p.x, y: p.y, sub: p.z < 0 });
  }
  return strokes.filter((s) => s.length > 0);
}

/** The square box of each of `n` symbols, side by side in the middle of the stage. */
export function symbolBoxes(n: number): Box[] {
  const size = Math.min(1100, Math.floor((STAGE_W - 200 - GAP * Math.max(0, n - 1)) / Math.max(1, n)));
  const total = n * size + GAP * Math.max(0, n - 1);
  const left = (STAGE_W - total) / 2;
  const top = (STAGE_H - size) / 2;
  return Array.from({ length: n }, (_, i) => ({ left: left + i * (size + GAP), top, size }));
}

/** A point of a symbol on the stage. */
export function stagePoint(box: Box, p: { x: number; y: number; sub: boolean }): TracePoint {
  const k = box.size / TEX;
  return { x: box.left + box.size / 2 + p.x * k, y: box.top + box.size / 2 - p.y * k, sub: p.sub };
}

/** Every stroke of every symbol, on the stage, in the order they are traced. */
export function traceStrokes(simbols: (Pick<DrawSimbol, "points"> | null)[]): { symbol: number; points: TracePoint[] }[] {
  const boxes = symbolBoxes(simbols.length);
  return simbols.flatMap((s, i) => strokesOf(s).map((st) => ({ symbol: i, points: st.map((p) => stagePoint(boxes[i], p)) })));
}

/** How close the finger must come to a point (main / sub-point), for a symbol box of that size. */
export function reach(box: Box, sub: boolean): number {
  return (sub ? 70 : 55) * Math.max(0.6, box.size / TEX);
}
